import { type ReactNode, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { MoveTargetWeek } from '../_lib/move-targets'
import type {
  CardDetailsSaveResult,
  RelatedProblemResult,
} from '../_state/contracts'
import { RELATED_KIND_LABEL } from './section-heading'

// Copied from `link-question-modal.tsx:66`'s `DialogContent` — the
// established narrower secondary-dialog width — rather than
// `card-details-modal.tsx`'s `CONTENT_CLASS`, which is sized for the primary
// modal's longer content (notes, history, its own day picker).
const CONTENT_CLASS =
  'coaching-dashboard gap-0 border-(--cd-border-4) bg-(--cd-panel) px-[18px] py-4 text-(--cd-text) shadow-[0_24px_60px_rgba(0,0,0,.6)] sm:max-w-[580px]'

/**
 * The `NoteEditor` idiom from `card-details-modal.tsx` (B3): a `role="status"`
 * line tracks an *outcome*, not just a failure, so a successful write says so
 * in the one place the coach is looking — a button that goes back to its
 * resting label and nothing else reads as "did that work?", and a second
 * click after a real success answers "…" is already in this plan", which
 * reads as a failure for a write that actually succeeded.
 */
type WriteOutcome = { kind: 'error' | 'success'; message: string } | null

function WriteOutcomeStatus(props: { outcome: WriteOutcome }): ReactNode {
  return (
    <span
      aria-live="polite"
      className={`text-[10px] ${props.outcome?.kind === 'error' ? 'text-(--cd-red)' : 'text-(--cd-text-faint)'}`}
      role="status"
    >
      {props.outcome?.message ?? ''}
    </span>
  )
}

export interface RelatedProblemModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The related problem the coach picked a chip for, or null when closed.
   * The wire shape (`CardDetailsResult['related'][number]`), not the server
   * DTO (LG-360 expert review, B7) — see `RelatedProblemResult`'s doc in
   * `_state/contracts.ts`.
   */
  related: RelatedProblemResult | null
  /**
   * Every day the related problem could be assigned to, built by the caller
   * with `buildMoveTargets` against a synthetic card (the related problem has
   * no card id of its own yet — see `curriculum-tab.tsx`).
   */
  moveTargets: readonly MoveTargetWeek[]
  onAddToTray: () => Promise<CardDetailsSaveResult>
  onAssignToDay: (
    weekIdx: number,
    dayIdx: number,
  ) => Promise<CardDetailsSaveResult>
  /**
   * Swaps the related problem in for the card currently open behind this
   * modal, on the picked day. Null on the mock (non-persisted) slice, which
   * doesn't implement the write — the button renders disabled with that
   * reason rather than vanishing, which would read as "there is nothing to
   * swap" instead of "not built yet".
   */
  onReplace:
    | ((weekIdx: number, dayIdx: number) => Promise<CardDetailsSaveResult>)
    | null
}

/**
 * The secondary modal a related-problem chip opens (LG-360 §8) — a duplicate,
 * variant or series sibling of the card open behind it, with the choice to
 * stage it to the tray, assign it to a day alongside the current card, or
 * swap it in for the current card.
 *
 * A **sibling** dialog of `CardDetailsModal`, not nested inside its
 * `DialogContent` — this is the first dialog-from-dialog in the repo, and
 * two simultaneously-open `Dialog`s would otherwise each paint their own
 * `DialogOverlay`, stacking the backdrop visibly twice as dark. Fixed with
 * `showOverlay={false}` on this dialog's own `DialogContent`
 * (`components/ui/dialog.tsx`) — the card modal's own overlay, permanently
 * up for as long as it is open, is the only one ever on screen. An earlier
 * attempt fixed this by toggling the *card* modal non-modal instead
 * (`modal={false}`) while this one was open; that was wrong at the root,
 * because Radix's `DialogContent` renders one of two entirely different
 * component types depending on `Dialog`'s own `modal` prop, and React
 * unmounts/remounts the whole subtree — including every `NoteEditor`'s
 * local draft state — on a type change at the same JSX position. This
 * dialog stays a normal Radix-modal `Dialog` throughout instead (its own
 * focus trap, `hideOthers`, and outside-pointer lock all still apply —
 * `@radix-ui/react-focus-scope`'s `focusScopesStack` and
 * `@radix-ui/react-dismissable-layer`'s layer stack are both module-level
 * singletons shared across every `Dialog` in the app, confirmed by reading
 * both packages' source, so this dialog's own trap correctly pauses the
 * card modal's while it is open and resumes it on close, and Escape closes
 * only the topmost layer regardless of either dialog's `modal` setting) —
 * `showOverlay` is the only thing distinguishing it from an ordinary modal.
 */
export function RelatedProblemModal(
  props: RelatedProblemModalProps,
): ReactNode {
  const {
    open,
    onOpenChange,
    related,
    moveTargets,
    onAddToTray,
    onAssignToDay,
    onReplace,
  } = props

  /**
   * What was on screen the last time this dialog was open — same idiom
   * `card-details-modal.tsx`'s `showing` uses. `DialogContent` animates out
   * over 200ms and Radix keeps it mounted for the whole of it, while the
   * caller clears `related` in the same commit it clears `open`; reading the
   * live prop during that window would flash the modal empty over every
   * ordinary close.
   */
  const lastOpen = useRef<{
    related: RelatedProblemResult | null
    moveTargets: readonly MoveTargetWeek[]
  } | null>(null)
  const snapshot = { related, moveTargets }
  if (open) lastOpen.current = snapshot
  const showing = open ? snapshot : lastOpen.current

  // Empty means "nothing picked yet" — the coach has not chosen a day.
  const [choice, setChoice] = useState('')
  const [addPending, setAddPending] = useState(false)
  const [addOutcome, setAddOutcome] = useState<WriteOutcome>(null)
  const [assignPending, setAssignPending] = useState(false)
  const [replacePending, setReplacePending] = useState(false)
  // Assign and Swap share one status line below (they always have), so they
  // share one outcome: whichever of the two ran most recently.
  const [writeOutcome, setWriteOutcome] = useState<WriteOutcome>(null)

  // Never opened, and not open now — there is nothing to animate out.
  if (showing === null) return null

  // `open` and `related` are two sources of truth for one thing: `open` is
  // the caller's intent, `related` is whether the row it points at still
  // resolves. When they disagree — the row stopped resolving while the
  // dialog is still meant to be open — returning null here would unmount the
  // whole `Dialog` with `open` still true and `onOpenChange` never fired, so
  // the caller's `relatedProblemId` would stay stuck on a dead id: the exact
  // hazard `CardDetailsModal` documents and guards against for `card === null`
  // (card-details-modal.tsx:596-616), mirrored here for `related === null`.
  if (showing.related === null) {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className={CONTENT_CLASS} showOverlay={false}>
          <DialogHeader className="gap-1.5 pr-8 text-left">
            <DialogTitle className="text-[13px] leading-snug font-bold">
              This related problem is no longer available
            </DialogTitle>
            <DialogDescription className="text-[10.5px] text-(--cd-text-faint)">
              It was removed, or the plan around it changed. Close this and
              reopen the card to see its related problems again.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  const item = showing.related
  // A related problem with no live `LearningItem` exists in the catalog but
  // is not assignable — nothing here would do anything but fail, so no
  // action buttons are offered at all, only the reason why.
  const assignable = item.learningItemId !== null

  const parsedChoice = (): [number, number] | null => {
    const [weekIdx, dayIdx] = choice.split(':').map(Number)
    if (weekIdx === undefined || Number.isNaN(weekIdx)) return null
    if (dayIdx === undefined || Number.isNaN(dayIdx)) return null
    return [weekIdx, dayIdx]
  }

  const handleAddToTray = async (): Promise<void> => {
    if (addPending) return
    setAddPending(true)
    setAddOutcome(null)
    const result = await onAddToTray()
    setAddPending(false)
    setAddOutcome(
      result.error === null
        ? {
            kind: 'success',
            message: `Added ${item.title} to the staging tray.`,
          }
        : { kind: 'error', message: result.error },
    )
  }

  const handleAssign = async (): Promise<void> => {
    const target = parsedChoice()
    if (target === null || assignPending) return
    setAssignPending(true)
    setWriteOutcome(null)
    const result = await onAssignToDay(target[0], target[1])
    setAssignPending(false)
    if (result.error === null) {
      setChoice('')
      setWriteOutcome({
        kind: 'success',
        message: `Assigned ${item.title} in addition to the current card.`,
      })
    } else {
      setWriteOutcome({ kind: 'error', message: result.error })
    }
  }

  const handleReplace = async (): Promise<void> => {
    const target = parsedChoice()
    if (target === null || onReplace === null || replacePending) return
    setReplacePending(true)
    setWriteOutcome(null)
    const result = await onReplace(target[0], target[1])
    setReplacePending(false)
    if (result.error === null) {
      setChoice('')
      setWriteOutcome({
        kind: 'success',
        message: `Swapped in ${item.title} instead of the current card.`,
      })
    } else {
      setWriteOutcome({ kind: 'error', message: result.error })
    }
  }

  const dayPickerId = `related-assign-to-${item.problemId}`

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className={CONTENT_CLASS} showOverlay={false}>
        <DialogHeader className="gap-1.5 pr-8 text-left">
          <span className="text-[9.5px] font-bold tracking-wide text-(--cd-text-faint) uppercase">
            {RELATED_KIND_LABEL[item.kind]}
            {item.kind === 'series' && item.seriesName
              ? ` · ${item.seriesName}`
              : ''}
          </span>
          <DialogTitle className="text-[13px] leading-snug font-bold text-balance">
            {item.title}
          </DialogTitle>
          <DialogDescription className="text-[10.5px] text-(--cd-text-faint)">
            {item.sourceDisplayName
              ? `${item.sourceDisplayName}${item.externalId != null ? ` · #${item.externalId}` : ''}`
              : 'No source on record'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-3">
          {!assignable ? (
            <p className="text-[11px] leading-relaxed text-(--cd-text-faint)">
              This problem is not in the catalog as an assignable item, so it
              cannot be staged, assigned, or swapped in.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <button
                  aria-label={`Add ${item.title} to the staging tray`}
                  className="cursor-pointer self-start rounded-md border-0 bg-(--cd-green) px-2.5 py-1 text-[10px] font-bold text-(--cd-accent-contrast) hover:bg-(--cd-green-hover) disabled:cursor-not-allowed disabled:opacity-45"
                  disabled={addPending}
                  onClick={() => void handleAddToTray()}
                  type="button"
                >
                  {addPending ? 'Adding…' : 'Add to the staging tray'}
                </button>
                <WriteOutcomeStatus outcome={addOutcome} />
              </div>

              {showing.moveTargets.length > 0 ? (
                <div className="flex flex-col gap-1.5 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <label
                      className="text-[10px] font-bold tracking-wide text-(--cd-text-faint) uppercase"
                      htmlFor={dayPickerId}
                    >
                      Assign to
                    </label>
                    <select
                      className="min-w-0 flex-1 rounded-md border border-(--cd-border-2) bg-(--cd-bg) px-1.5 py-1 text-[10.5px] text-(--cd-text) focus:border-(--cd-border-4) focus:outline-none"
                      id={dayPickerId}
                      onChange={(event) => setChoice(event.currentTarget.value)}
                      value={choice}
                    >
                      {/* Every day here reports `current: false` — the
                          synthetic card `curriculum-tab.tsx` builds this
                          picker against has an empty id, which cannot match
                          any card on the board. Correct: this problem is
                          unassigned, so it sits on no day yet. */}
                      <option value="">Pick a day…</option>
                      {showing.moveTargets.map((week) => (
                        <optgroup key={week.weekId} label={week.label}>
                          {week.days.map((day) => (
                            <option
                              disabled={day.disabledReason !== null}
                              key={day.value}
                              value={day.value}
                            >
                              {day.label}
                              {day.disabledReason !== null
                                ? ` — ${day.disabledReason}`
                                : ''}
                              {day.disabledReason === null && day.overBudget
                                ? ' — over budget'
                                : ''}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  {/* Both writes are explicit buttons rather than the
                      select's `onChange` — same reasoning as `AssignedDayPicker`
                      in `card-details-modal.tsx`: Radix hands the first
                      tabbable element in the dialog opening focus, and an
                      arrow key on a focused select changes its value outright
                      on Windows Chrome. A coach who opened this modal and
                      pressed an arrow key would otherwise assign or swap
                      sight-unseen. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      aria-label={`Assign ${item.title} in addition to the current card`}
                      className="cursor-pointer rounded-md border-0 bg-(--cd-green) px-2.5 py-1 text-[10px] font-bold text-(--cd-accent-contrast) hover:bg-(--cd-green-hover) disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={choice === '' || assignPending}
                      onClick={() => void handleAssign()}
                      type="button"
                    >
                      {assignPending ? 'Assigning…' : 'Assign in addition'}
                    </button>
                    <button
                      aria-label={`Swap in ${item.title} instead of the current card`}
                      className="cursor-pointer rounded-md border border-(--cd-border-2) bg-transparent px-2.5 py-1 text-[10px] font-bold text-(--cd-text) hover:border-(--cd-border-4) disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={
                        onReplace === null || choice === '' || replacePending
                      }
                      onClick={() => void handleReplace()}
                      type="button"
                    >
                      {replacePending ? 'Swapping…' : 'Swap in instead of'}
                    </button>
                  </div>
                  {onReplace === null ? (
                    <p className="text-[10px] text-(--cd-text-faint)">
                      Swapping a related problem in for the current card is not
                      built yet.
                    </p>
                  ) : null}
                  <WriteOutcomeStatus outcome={writeOutcome} />
                </div>
              ) : null}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
